import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';

import type { AnnouncementContentProps } from '../types';
import creationFlowImage from './creation-flow.webp';
import jobProfileTemplateImage from './job-profile-template.webp';
import skillArchitectureFilterImage from './skill-architecture-filter.webp';

type Step = {
    readonly ctaKey?: TranslationKey;
    readonly ctaRoute?: string;
    readonly descriptionKey: TranslationKey;
    readonly headlineKey: TranslationKey;
    readonly image: string;
    readonly titleKey: TranslationKey;
};

const STEPS: readonly Step[] = [
    {
        descriptionKey: 'announcements.horizon_1_2.job_profile_template.description',
        headlineKey: 'announcements.horizon_1_2.job_profile_template.headline',
        image: jobProfileTemplateImage,
        titleKey: 'announcements.horizon_1_2.job_profile_template.title',
    },
    {
        ctaKey: 'announcements.horizon_1_2.skill_architecture.cta',
        ctaRoute: '/skills',
        descriptionKey: 'announcements.horizon_1_2.skill_architecture.description',
        headlineKey: 'announcements.horizon_1_2.skill_architecture.headline',
        image: skillArchitectureFilterImage,
        titleKey: 'announcements.horizon_1_2.skill_architecture.title',
    },
    {
        descriptionKey: 'announcements.horizon_1_2.creation_flow.description',
        headlineKey: 'announcements.horizon_1_2.creation_flow.headline',
        image: creationFlowImage,
        titleKey: 'announcements.horizon_1_2.creation_flow.title',
    },
];

type SidebarStepProps = {
    readonly index: number;
    readonly isActive: boolean;
    readonly onClick: () => void;
    readonly title: string;
};

const SidebarStep: FunctionComponent<SidebarStepProps> = ({ index, isActive, onClick, title }) => (
    <button
        className={clsx(
            'grid grid-cols-[20px_auto] gap-x-m items-baseline text-left border-none rounded-sm p-s px-xs bg-transparent cursor-pointer transition duration-200 font-[inherit]',
        )}
        type="button"
        onClick={onClick}
    >
        <span className={clsx('text-xs tabular-nums', isActive ? 'font-semibold text-black-900' : 'text-gray-400')}>
            {index + 1}.
        </span>
        <span
            className={clsx(
                'text-sm font-medium whitespace-nowrap transition duration-200',
                isActive ? 'text-black-900' : 'text-gray-600',
            )}
        >
            {title}
        </span>
    </button>
);

export const Horizon12Content: FunctionComponent<AnnouncementContentProps> = ({ onDismiss, onNavigate }) => {
    const translate = useTranslate();
    const [step, setStep] = useState(0);

    const handleBack = useCallback(() => setStep((s) => s - 1), []);
    const handleNext = useCallback(() => setStep((s) => s + 1), []);

    return (
        <div className="flex max-h-[600px]">
            <div className="w-fit shrink-0 bg-gray-100 border-r border-gray-300 flex flex-col justify-between p-3xl pr-5xl">
                <div>
                    <div className="mb-l">
                        <Badge variant="orange">{translate('announcements.whats_new')}</Badge>
                    </div>
                    <div className="flex flex-col gap-xs items-start">
                        {STEPS.map((s, i) => (
                            <SidebarStep
                                key={s.titleKey}
                                index={i}
                                isActive={step === i}
                                title={translate(s.titleKey)}
                                onClick={() => setStep(i)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col bg-white-200">
                <div className="flex-1 overflow-hidden p-3xl pb-l">
                    <div className="grid">
                        {STEPS.map((feature, i) => {
                            const { ctaKey, ctaRoute } = feature;

                            return (
                                <div
                                    key={feature.titleKey}
                                    className="col-start-1 row-start-1"
                                    style={{ visibility: step === i ? 'visible' : 'hidden' }}
                                >
                                    <h2 className="text-xl font-normal tracking-tight text-black-900 m-0 leading-[1.2]">
                                        {translate(feature.headlineKey)}
                                    </h2>
                                    <p className="text-sm font-normal text-gray-700 m-0 mt-xl leading-relaxed">
                                        {translate(feature.descriptionKey)}
                                        {!is.undefined(ctaKey) && !is.undefined(ctaRoute) && (
                                            <>
                                                {' '}
                                                <button
                                                    className="text-orange-500 underline cursor-pointer hover:text-orange-700 transition duration-200 inline whitespace-nowrap bg-transparent border-none p-0 font-[inherit] text-sm"
                                                    type="button"
                                                    onClick={() => onNavigate(ctaRoute)}
                                                >
                                                    {translate(ctaKey)} &rarr;
                                                </button>
                                            </>
                                        )}
                                    </p>
                                    <div className="border-2 border-gray-300 overflow-hidden mt-5xl rounded-lg">
                                        <img
                                            alt={translate(feature.titleKey)}
                                            className="w-full h-auto block"
                                            src={feature.image}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-s px-xxl pb-xxl pt-s">
                    {step > 0 && (
                        <Button variant="tertiary" onClick={handleBack}>
                            {translate('announcements.back')}
                        </Button>
                    )}
                    {step < STEPS.length - 1 ? (
                        <Button onClick={handleNext}>{translate('announcements.next')}</Button>
                    ) : (
                        <Button type="button" onClick={onDismiss}>
                            {translate('announcements.get_started')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ANNOUNCEMENT_CONTENT: Record<string, FunctionComponent<AnnouncementContentProps>> = {
    horizon_1_2: Horizon12Content,
};
